export const INTEGRATION_DOCUMENTATION_BASE_URL =
  'https://docs.kognitos.com/guides/platform/integrations';

/** The book catalog only changes when an integration is installed or removed. */
export const BOOK_CATALOG_STALE_TIME = 5 * 60 * 1000;

/** Matches the library's asset and collection page size. */
export const BOOK_CATALOG_PAGE_SIZE = 25;

/**
 * Integration names excluded from Connections page display.
 * These have dedicated UIs (e.g., email trigger) and should not count toward empty state.
 */
export const EXCLUDED_APP_NAMES = ['email'];

/** Whether a book is one of {@link EXCLUDED_APP_NAMES}, compared case-insensitively. */
export const isExcludedBookName = (name?: string): boolean =>
  EXCLUDED_APP_NAMES.some(
    excluded => name?.toLowerCase() === excluded.toLowerCase(),
  );

/**
 * Books in this list expose only their latest registered version for creating
 * new connections. Legacy connections remain manageable from their detail
 * pages and the workspace connection list.
 */
export const LATEST_ONLY_NEW_CONNECTION_BOOK_NAMES = ['browser'] as const;

/** Single starter template: label key for the chip, prompt key for the message sent on click. */
export interface IAssistantStarterTemplate {
  id: string;
  labelKey: string;
  promptKey: string;
}

/**
 * Which flow the assistant is running in. Drives the starter chips and header
 * copy so an empty conversation prompts the user with relevant questions.
 */
export type TAssistantVariant =
  | 'connect'
  | 'diagnosis'
  | 'discover'
  | 'trigger';

/**
 * Static starter chips shown in the Integration Assistant panel on an empty
 * conversation, until the agent emits dynamic follow-up suggestions. Keyed by
 * flow so the questions match what the agent can actually do. Copy lives in
 * i18n so labels/prompts can change without code edits.
 */
export const INTEGRATION_ASSISTANT_STARTERS: Record<
  TAssistantVariant,
  IAssistantStarterTemplate[]
> = {
  connect: [
    {
      id: 'walk-me-through',
      labelKey: 'integrations.assistant.panel.starters.walkMeThrough.label',
      promptKey: 'integrations.assistant.panel.starters.walkMeThrough.prompt',
    },
    {
      id: 'find-credentials',
      labelKey: 'integrations.assistant.panel.starters.findCredentials.label',
      promptKey: 'integrations.assistant.panel.starters.findCredentials.prompt',
    },
    {
      id: 'which-auth-method',
      labelKey: 'integrations.assistant.panel.starters.whichAuthMethod.label',
      promptKey: 'integrations.assistant.panel.starters.whichAuthMethod.prompt',
    },
  ],
  diagnosis: [
    {
      id: 'why-failing',
      labelKey: 'integrations.assistant.panel.starters.whyFailing.label',
      promptKey: 'integrations.assistant.panel.starters.whyFailing.prompt',
    },
    {
      id: 'check-health',
      labelKey: 'integrations.assistant.panel.starters.checkHealth.label',
      promptKey: 'integrations.assistant.panel.starters.checkHealth.prompt',
    },
    {
      id: 'fix-credentials',
      labelKey: 'integrations.assistant.panel.starters.fixCredentials.label',
      promptKey: 'integrations.assistant.panel.starters.fixCredentials.prompt',
    },
  ],
  discover: [
    {
      id: 'what-can-i-do',
      labelKey: 'integrations.assistant.panel.starters.whatCanIDo.label',
      promptKey: 'integrations.assistant.panel.starters.whatCanIDo.prompt',
    },
    {
      id: 'pick-for-use-case',
      labelKey: 'integrations.assistant.panel.starters.pickForUseCase.label',
      promptKey: 'integrations.assistant.panel.starters.pickForUseCase.prompt',
    },
    {
      id: 'compare-methods',
      labelKey: 'integrations.assistant.panel.starters.compareMethods.label',
      promptKey: 'integrations.assistant.panel.starters.compareMethods.prompt',
    },
  ],
  trigger: [
    {
      id: 'list-available-values',
      labelKey:
        'integrations.assistant.panel.starters.listAvailableValues.label',
      promptKey:
        'integrations.assistant.panel.starters.listAvailableValues.prompt',
    },
    {
      id: 'fill-for-me',
      labelKey: 'integrations.assistant.panel.starters.fillForMe.label',
      promptKey: 'integrations.assistant.panel.starters.fillForMe.prompt',
    },
    {
      id: 'explain-fields',
      labelKey: 'integrations.assistant.panel.starters.explainFields.label',
      promptKey: 'integrations.assistant.panel.starters.explainFields.prompt',
    },
  ],
};

/**
 * Single source of truth for which flow the assistant is in, so the greeting
 * (hook), and the starter chips + header copy (panel), never diverge:
 * - a connection in scope + `mode === 'discover'` → discovery
 * - a connection in scope + `mode === 'trigger'`   → trigger (poll/webhook) setup
 * - a connection in scope, any other mode          → diagnosis
 * - no connection in scope                          → connect/setup
 */
export function resolveAssistantVariant(
  connectionId: string | undefined,
  mode: 'discover' | 'trigger' | undefined,
): TAssistantVariant {
  if (!connectionId) return 'connect';
  if (mode === 'discover') return 'discover';
  if (mode === 'trigger') return 'trigger';
  return 'diagnosis';
}
